import { useEffect, useState } from "react";
import { z } from "zod";

import { httpGet } from "../lib/http";
import { BADGE_TONE } from "../ui/classes";
import { Spinner } from "../ui/Spinner";

const SessionSummarySchema = z.object({
  sessionId: z.string(),
  projectId: z.number(),
  createdAt: z.string(),
  title: z.string().optional(),
  // Phase 9 surfaces `title` and `live`; the legacy fallback path still
  // returns the older `firstMessage` + `endedAt` shape.
  endedAt: z.string().nullable().optional(),
  firstMessage: z.string().nullable().optional(),
  live: z.boolean(),
});
type SessionSummary = z.infer<typeof SessionSummarySchema>;

const SessionsListSchema = z.object({
  sessions: z.array(SessionSummarySchema),
});

interface HistorySidebarProps {
  readonly activeSessionId?: string;
  readonly onPick: (sessionId: string) => void;
}

export function HistorySidebar({ activeSessionId, onPick }: HistorySidebarProps) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ready"; sessions: SessionSummary[] }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    httpGet("/sessions?mine=true", SessionsListSchema)
      .then(({ sessions }) => {
        if (!cancelled) setState({ kind: "ready", sessions });
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, [activeSessionId]);

  return (
    <aside className="w-64 shrink-0 border-r border-line bg-surface p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
        Recent sessions
      </div>
      {state.kind === "loading" && <Spinner label="Loading…" />}
      {state.kind === "error" && (
        <p className="text-xs text-danger-fg-bright">Failed to load: {state.message}</p>
      )}
      {state.kind === "ready" && state.sessions.length === 0 && (
        <p className="text-xs text-ink-subtle">No prior sessions.</p>
      )}
      {state.kind === "ready" && (
        <ul className="space-y-1">
          {state.sessions.map((s) => (
            <li key={s.sessionId}>
              <button
                type="button"
                onClick={() => onPick(s.sessionId)}
                className={`w-full rounded-md px-2 py-1 text-left text-xs hover:bg-surface-3 ${
                  s.sessionId === activeSessionId ? "bg-surface-3 font-medium" : ""
                }`}
              >
                <div className="flex items-center gap-1">
                  <span className="font-mono text-ink-subtle">#{s.projectId}</span>
                  {s.live && (
                    <span className={`rounded-full px-1.5 text-[10px] font-medium ${BADGE_TONE.success}`}>
                      live
                    </span>
                  )}
                </div>
                <div className="truncate text-ink-3">
                  {s.title ?? s.firstMessage ?? "(empty)"}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}
